import { ResponsiveStylesStructured } from '@duda-co/responsive-styles/types/ResponsiveStylesTypes';
import { ResponsiveStylesProp } from 'client/widget-components/responsive-styles/responsiveStylesService';

export enum WidgetStep {
    STAFF_MEMBER_SELECTION = 'staff-member-selection-step',
    EVENT_SELECTION = 'event-selection-step',
    BOOKER = 'booker-step',
}

export enum CalBookerStep {
    SELECTING_TIME = 'selecting_time',
    BOOKING = 'booking',
}

export enum DesignStyle {
    SITE_THEME = 'site-theme',
    BASIC = 'basic',
}

export enum PricingType {
    FREE = 'FREE',
    PAID = 'PAID',
}

export interface FeatureMetadata {
    isNativeBookingAvailable: boolean;
    isNativeBookingPublishingAvailable: boolean;
    isPaidNativeBookingAvailable: boolean;
}

export enum BookingSource {
    WEBSITE = 'website',
    DIRECTORY = 'directory',
}

export interface PriceInfo {
    pricingType: PricingType;
    basePrice: number | null;
    formattedBasePrice: string | null;
    formattedTaxIncludedPrice: string | null;
    taxIncludedPrice: number | null;
    taxPercentage: number | null;
    taxIncluded: boolean;
}

export interface BookerResponsiveStyles {
    bookerPrimaryColor?: ResponsiveStylesStructured;
    bookerSecondaryColor?: ResponsiveStylesStructured;
    bookerBackgroundColor?: ResponsiveStylesStructured;
    bookerTitle?: ResponsiveStylesStructured;
    bookerSubtitle?: ResponsiveStylesStructured;
    bookerBody?: ResponsiveStylesStructured;
}

export interface BookerContextType extends FeatureMetadata {
    eventSlug: string | null;
    teamId: number | null;
    userName: string | null;
    rescheduleUid?: string | null;
    cancelBookingId?: string | null;
    widgetId: string;
    designStyle: DesignStyle;
    language: string;
    apiUrl: string;
    clientId: string;
    organizationId: number;
    eventIdentifier: string;
    isBookingActive: boolean;
    isBookingDryRun: boolean;
    events: EventServerModel[];
    groupedAppointmentInfo: GroupedAppointmentInfo;
    serviceProviderStage: ServiceProviderStage;
    currentEvent: EventServerModel | null;
    /** When false, omit free-booking price labels. Null/undefined defaults to showing free price. */
    showFreePrice?: boolean | null;
    /** Host-resolved native-booker toggle (see CalBookingProps.isNativeBookerEnabled). */
    isNativeBookerEnabled: boolean;
    styles: CalBookingStyles;
    bookingSource: BookingSource;
}

interface Attendee {
    name: string;
    email: string;
    timeZone: string;
}

interface SeatReferenceAttendee {
    name: string;
    email: string;
    displayEmail: string;
    timeZone: string;
    language: string;
    absent: boolean;
    seatUid: string;
    bookingFieldsResponses: Record<string, unknown>;
    metadata: Record<string, string>;
}

interface BookingHost {
    id: number;
    name: string;
    // Null when the merchant turns on "Hide organizer's email" (SNIPDUDE-8138).
    email: string | null;
    displayEmail: string | null;
    username: string | null;
}

interface RegularBookingUser {
    avatarUrl: string | null;
    email: string | null;
    id: number;
    name: string;
    timeZone: string;
    username: string | null;
}

interface BaseBooking {
    /** The booker's own answers: `notes` is rendered, `location` tells a join link from a typed address. */
    responses?: { notes?: string; location?: { value?: string } };
    title: string;
    userPrimaryEmail: string;
    startTime: string;
    videoCallUrl?: string;
    location?: string;
    uid: string;
    endTime: string;
    status: BookingStatus;
    metadata?: Record<string, string>;
    hosts: BookingHost[];
}

export interface SeatReferenceBooking extends BaseBooking {
    start: string;
    end: string;
    attendees: Array<SeatReferenceAttendee>;
}

export interface RegularBooking extends BaseBooking {
    user: RegularBookingUser;
    attendees: Array<Attendee>;
}

export type Booking = SeatReferenceBooking | RegularBooking;

export type CustomerNameResponse =
    | string
    | { firstName: string; lastName: string };

export interface CreateBookingInput {
    eventTypeId: number;
    timeZone: string;
    language: string;
    metadata: Record<string, string>;
    responses: Record<
        string,
        string | { value: string; optionValue: string } | CustomerNameResponse
    >;
    slot_duration: number;
    start: string;
    end: string;
    rrHostSubsetIds?: number[];
}

export enum UrlAction {
    RESCHEDULE = 'reschedule',
    CANCEL = 'cancel',
}

export enum BookingStatus {
    CANCELLED = 'CANCELLED',
    PENDING = 'PENDING',
    RESCHEDULED = 'RESCHEDULED',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface CalOrganization {
    id: number;
    name: string;
    slug: string;
    bannerUrl: string | null;
}

export interface CalUserProfile {
    id: number | null;
    upId: string;
    username: string;
    organizationId: number | null;
    organization: CalOrganization | null;
}

export interface CalUser {
    username: string;
    name: string;
    avatarUrl: string | null;
    weekStart: string;
    organizationId: number | null;
    bookerUrl: string;
    image?: string;
    brandColor?: string | null;
    darkBrandColor?: string | null;
    theme?: string | null;
    bookerLayouts?: CalBookerLayouts | null;
}

export interface CalUserWithProfile extends CalUser {
    profile: CalUserProfile;
}

export interface CalHost {
    user: {
        username: string;
    };
}

export interface CalEntity {
    fromRedirectOfNonOrgLink: boolean;
    considerUnpublished: boolean;
    orgSlug: string;
    teamSlug: string;
    name: string;
    hideProfileLink: boolean;
    logoUrl: string;
}

export interface CalBookerLayouts {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

export interface CalParent {
    slug: string;
    name: string;
    bannerUrl: string;
    logoUrl: string;
}

export interface CalSchedule {
    id: string;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
}

export interface CalLocation {
    type: string;
    [key: string]: any;
}

export interface CalCustomInput {
    [key: string]: any;
}

export interface CalMetadata {
    disableStandardEmails?: {
        all?: {
            host?: boolean;
        };
    };
    apps?: {
        [appName: string]: {
            enabled?: boolean;
            thankYouPage?: string;
            [key: string]: any;
        };
    };
    [key: string]: any;
}

export interface CalFieldTranslation {
    [key: string]: any;
}

export interface CalRecurringEvent {
    [key: string]: any;
}

export interface CalBookingFieldSource {
    id: string;
    type: string;
    label: string;
}

export interface CalBookingFieldVariant {
    name: string;
    type: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
}

export interface CalBookingFieldVariantsConfig {
    variants: {
        [variantName: string]: {
            fields: CalBookingFieldVariant[];
        };
    };
}

export interface CalBookingFieldOptionsInputs {
    [optionName: string]: {
        type: string;
        required?: boolean;
        placeholder?: string;
    };
}

export interface CalBookingFieldView {
    label: string;
    id: string;
}

export interface CalBookingField {
    name: string;
    type: string;
    defaultLabel: string;
    defaultPlaceholder?: string;
    required: boolean;
    hidden?: boolean;
    variant?: string;
    variantsConfig?: CalBookingFieldVariantsConfig;
    editable?: string;
    sources: CalBookingFieldSource[];
    disableOnPrefill?: boolean;
    getOptionsAt?: string;
    optionsInputs?: CalBookingFieldOptionsInputs;
    hideWhenJustOneOption?: boolean;
    views?: CalBookingFieldView[];
}

export interface CalTeamParent {
    slug: string;
    name: string;
    bannerUrl: string | null;
    logoUrl: string | null;
}

export interface CalTeam {
    parentId?: number;
    metadata?: any;
    brandColor?: string | null;
    darkBrandColor?: string | null;
    slug: string;
    name: string;
    logoUrl?: string | null;
    theme?: string | null;
    hideTeamProfileLink?: boolean;
    parent?: CalTeamParent;
    isPrivate?: boolean;
}

export interface CalWorkflow {
    [key: string]: any;
}

export interface CalOwner {
    [key: string]: any;
}

export interface CalInstantMeetingParameter {
    [key: string]: any;
}

export interface CalAiPhoneCallConfig {
    [key: string]: any;
}

export interface CalSubsetOfHostUser {
    id: number;
    avatarUrl: string | null;
    username: string;
    name: string;
    weekStart: string;
    brandColor: string | null;
    darkBrandColor: string | null;
    theme: string | null;
    metadata: any;
    organization: CalOrganization;
    defaultScheduleId: number;
    nonProfileUsername: string;
    profile: CalUserProfile;
}

export interface Host {
    avatarUrl: string | null;
    mandatory: boolean;
    name: string;
    priority: string;
    userId: number;
    username: string;
}

export interface CalSubsetOfHost {
    user: CalSubsetOfHostUser;
}

export interface CalEventData {
    id: number;
    title: string;
    description: string;
    interfaceLanguage: string | null;
    eventName: string | null;
    slug: string;
    isInstantEvent: boolean;
    instantMeetingParameters: CalInstantMeetingParameter[];
    aiPhoneCallConfig: CalAiPhoneCallConfig | null;
    schedulingType: string;
    length: number;
    lengthInMinutes: number;
    locations: CalLocation[];
    customInputs: CalCustomInput[];
    disableGuests: boolean;
    hideOrganizerEmail: boolean;
    metadata: CalMetadata;
    lockTimeZoneToggleOnBookingPage: boolean;
    lockedTimeZone: string | null;
    requiresConfirmation: boolean;
    autoTranslateDescriptionEnabled: boolean;
    fieldTranslations: CalFieldTranslation[];
    requiresBookerEmailVerification: boolean;
    recurringEvent: CalRecurringEvent | null;
    price: number;
    currency: string;
    seatsPerTimeSlot: number | null;
    disableCancelling: boolean;
    disableRescheduling: boolean;
    allowReschedulingCancelledBookings: boolean;
    seatsShowAvailabilityCount: boolean;
    bookingFields: CalBookingField[];
    teamId: number;
    team: CalTeam;
    successRedirectUrl: string | null;
    forwardParamsSuccessRedirect: boolean;
    workflows: CalWorkflow[];
    owner: CalOwner | null;
    schedule: CalSchedule | null;
    instantMeetingSchedule: CalSchedule | null;
    periodType: string;
    periodDays: number | null;
    periodEndDate: string | null;
    periodStartDate: string | null;
    periodCountCalendarDays: boolean | null;
    hidden: boolean;
    assignAllTeamMembers: boolean;
    rescheduleWithSameRoundRobinHost: boolean;
    parent: CalParent | null;
    subsetOfHosts: CalSubsetOfHost[];
    hosts: Host[];
    bookerLayouts: CalBookerLayouts | null;
    profile: CalUser;
    subsetOfUsers: CalUserWithProfile[];
    entity: CalEntity;
    isDynamic: boolean;
    showInstantEventConnectNowModal: boolean;
    seats?:
        | { disabled: true }
        | {
              disabled: false;
              seatsPerTimeSlot: number;
              showAttendeeInfo: boolean;
              showAvailabilityCount: boolean;
          };
}

export interface CalEventDataWithDisplayPrice extends CalEventData {
    displayPrice: string;
}
export interface BookerStoreValues {
    state: string;
}

export interface EventServerModel {
    identifier: string;
    slug: string;
    calEventId: number;
    priceInfo: PriceInfo;
}

export interface GroupedAppointmentInfo {
    title: string;
    isTitleVisible: boolean;
}

export interface ServiceProviderStage extends GroupedAppointmentInfo {
    isStageShown: boolean;
}

export interface CalBookingStyles extends BookerResponsiveStyles {
    selectionListWrapper?: ResponsiveStylesProp;
}

export interface CalBookingProps {
    clientId: string;
    userName: string;
    localeCode?: any;
    teamId: number;
    featureMetadata: FeatureMetadata;
    widgetId: string;
    designStyle: DesignStyle | null;
    calApiDomain: string;
    /**
     * Engine-resolved Cal API base URL (ms-booking). Absolute for Cal.com
     * (e.g. https://api.cal.com), relative for the native booking engine
     * (e.g. /ms/booking-engine/public). Falls back to calApiDomain when absent.
     */
    calApiBaseUrl?: string;
    organizationId: number;
    events: EventServerModel[] | null;
    groupedAppointmentInfo: GroupedAppointmentInfo;
    serviceProviderStage: ServiceProviderStage;
    editorStep?: WidgetStep | null;
    /** When false, omit free-booking price labels. Null/undefined defaults to true (show). */
    showFreePrice?: boolean | null;
    /**
     * Host-resolved native-booker toggle (LaunchDarkly flag
     * `runtime.ssr.native.booker.enabled`, resolved by the monolith). When true,
     * render the native `@duda-co/booking` path. Null/undefined defaults to false.
     */
    isNativeBookerEnabled?: boolean | null;
    _styles: CalBookingStyles;

    // @deprecated use events instead
    price: number | null;
    // @deprecated use events instead
    pricingType: PricingType;
    // @deprecated use events instead
    eventSlug: string;
    // @deprecated use events instead
    eventIdentifier: string;
    // @deprecated use events instead
    calEventId: number;
    // @deprecated use events instead
    priceInfo: PriceInfo;

    bookingSource: BookingSource;
}
